import React, { useEffect, useRef, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import styles from './Drawer.module.scss';

export interface DrawerProps {
  /** 是否显示抽屉 */
  open: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 抽屉方向 */
  anchor?: 'left' | 'right' | 'top' | 'bottom';
  /** 抽屉宽度（left/right）或高度（top/bottom） */
  size?: number | string;
  /** 是否显示遮罩层 */
  backdrop?: boolean;
  /** 点击遮罩层是否关闭 */
  closeOnBackdropClick?: boolean;
  /** 按 ESC 键是否关闭 */
  closeOnEscape?: boolean;
  /** 是否保持挂载（不销毁 DOM） */
  keepMounted?: boolean;
  /** 挂载容器 */
  container?: HTMLElement | (() => HTMLElement) | null;
  /** 层级 */
  zIndex?: number;
  /** 过渡动画时长（毫秒） */
  transitionDuration?: number;
  /** 子内容 */
  children: React.ReactNode;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
}

const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  anchor = 'right',
  size = 320,
  backdrop = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  keepMounted = false,
  container = () => document.body, // 默认使用 Portal 模式
  zIndex = 1300,
  transitionDuration = 225,
  children,
  className,
  style,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // 动画状态管理
  const [isVisible, setIsVisible] = useState(false); // 初始为 false
  const [isAnimating, setIsAnimating] = useState(false);

  // 处理打开/关闭状态变化
  useEffect(() => {
    if (open) {
      setIsVisible(true);
      // 使用 setTimeout 而不是 requestAnimationFrame，确保 DOM 更新完成
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 10); // 小延迟确保 DOM 渲染完成
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // 等待关闭动画完成后再隐藏组件
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, transitionDuration);
      return () => clearTimeout(timer);
    }
  }, [open, transitionDuration]);

  // 处理 ESC 键关闭
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && closeOnEscape && open) {
      onClose();
    }
  }, [closeOnEscape, onClose, open]);

  // 处理遮罩点击关闭
  const handleBackdropClick = useCallback((event: React.MouseEvent) => {
    if (
      event.target === event.currentTarget &&
      closeOnBackdropClick &&
      backdrop
    ) {
      onClose();
    }
  }, [closeOnBackdropClick, backdrop, onClose]);

  // 监听键盘事件
  useEffect(() => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, handleKeyDown]);

  // 控制 body 滚动（仅在使用 Portal 且有遮罩时）
  useEffect(() => {
    const isPortal = container !== null;
    if (isPortal && backdrop && isAnimating) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [container, backdrop, isAnimating]);

  // 如果不可见且不保持挂载，则不渲染
  if (!isVisible && !keepMounted) {
    return null;
  }

  // 计算容器元素
  const getContainer = (): HTMLElement => {
    if (container === null) return document.body;
    return typeof container === 'function' ? container() : container;
  };

  // 计算抽屉样式
  const drawerStyle: React.CSSProperties = {
    ...style,
    zIndex,
    transitionDuration: `${transitionDuration}ms`,
    ...(anchor === 'left' || anchor === 'right'
      ? { width: size }
      : { height: size }
    ),
  };

  // 计算遮罩样式
  const backdropStyle: React.CSSProperties = {
    zIndex: zIndex - 1,
    transitionDuration: `${transitionDuration}ms`,
  };

  const isInline = container === null;

  // 根容器类名
  const rootClassName = classNames(
    styles.root,
    {
      [styles.rootOpen]: isAnimating,
    }
  );

  // 遮罩类名
  const backdropClassName = classNames(
    styles.backdrop,
    {
      [styles.backdropOpen]: isAnimating,
      [styles.backdropHidden]: !backdrop,
    }
  );

  // 抽屉类名
  const drawerClassName = classNames(
    styles.drawer,
    styles[`drawer${anchor.charAt(0).toUpperCase() + anchor.slice(1)}`],
    {
      [styles.drawerOpen]: isAnimating, // 只有动画激活时才显示
    },
    className
  );

  const drawerContent = (
    <div
      className={rootClassName}
      style={{
        zIndex,
        position: isInline ? 'absolute' : 'fixed'
      }}
    >
      {/* 遮罩层 */}
      {backdrop && (
        <div
          ref={backdropRef}
          className={backdropClassName}
          style={{
            ...backdropStyle,
            position: isInline ? 'absolute' : 'fixed'
          }}
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* 抽屉主体 */}
      <div
        ref={drawerRef}
        className={drawerClassName}
        style={{
          ...drawerStyle,
          position: isInline ? 'absolute' : 'fixed'
        }}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        {/* 只有在可见或保持挂载时才渲染子内容 */}
        {(isVisible || keepMounted) && children}
      </div>
    </div>
  );

  // 决定渲染方式
  if (container === null) {
    // 内联渲染
    return drawerContent;
  }

  // Portal 渲染
  return ReactDOM.createPortal(drawerContent, getContainer());
};

export default Drawer;
